import { z } from 'zod';

/** Cosa può fare l'agente di una scheda nel suo giro quotidiano. */
export const AGENT_ROUTINES = [
  { id: 'sito', label: 'Controlla il sito', detail: 'Risponde, velocità, certificato' },
  { id: 'numeri', label: 'Guarda i numeri', detail: 'Segnala cali e crescite di social e Google' },
  { id: 'recensioni', label: 'Prepara le risposte alle recensioni', detail: 'Bozze da copiare, mai pubblicate da sole' },
  { id: 'post', label: 'Prepara i post', detail: 'Se la pagina è indietro, scrive bozze nel Piano' },
  { id: 'attivita', label: 'Tiene d’occhio le attività', detail: 'Ricorda quelle scadute o in scadenza' },
  { id: 'idee', label: 'Cerca idee sul web', detail: 'Novità del settore e spunti per i contenuti' },
] as const;
export type AgentRoutineId = (typeof AGENT_ROUTINES)[number]['id'];
export const AGENT_ROUTINE_IDS = AGENT_ROUTINES.map((r) => r.id) as [AgentRoutineId, ...AgentRoutineId[]];

export const AGENT_MODELS = [
  { id: 'sonnet', label: 'Sonnet (veloce, consuma meno limiti)' },
  { id: 'opus', label: 'Opus (il più capace)' },
] as const;
export type AgentModel = (typeof AGENT_MODELS)[number]['id'];

export interface AgentAction {
  id: string;
  name: string;
  description: string;
  url: string;
  needsConfirm: boolean;
  hasSecret: boolean;
}

export interface AgentConfig {
  pageId: string;
  enabled: boolean;
  name: string;
  instructions: string;
  routine: Record<AgentRoutineId, boolean>;
  /** Orario del giro quotidiano, HH:MM (Europe/Rome). */
  runTime: string;
  model: AgentModel;
  /** Prepara bozze, attività e risposte da solo (niente viene pubblicato). */
  autonomous: boolean;
  /** Appunti che l'agente tiene per i giorni successivi. */
  memory: string;
  actions: AgentAction[];
}

export type AgentRunKind = 'giro' | 'manuale' | 'attivita';
export type AgentRunStatus = 'in_coda' | 'in_corso' | 'fatto' | 'errore';

export interface AgentRun {
  id: string;
  pageId: string;
  kind: AgentRunKind;
  taskId: string | null;
  status: AgentRunStatus;
  report: string;
  urgent: boolean;
  tools: Array<{ name: string; label: string }>;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface AgentPending {
  id: string;
  pageId: string;
  tool: string;
  summary: string;
  status: 'in_attesa' | 'fatto' | 'rifiutato' | 'errore';
  result: string | null;
  createdAt: string;
}

export interface AgentDiaryEntry {
  id: string;
  at: string;
  title: string;
  body: string;
}

export interface AgentDetail {
  config: AgentConfig;
  runs: AgentRun[];
  pending: AgentPending[];
  /** Ultime voci del diario (giri, conversazioni, decisioni), dalla più recente. */
  diary: AgentDiaryEntry[];
  nextRunAt: string | null;
}

export interface AgentOverview {
  pageId: string;
  pageName: string;
  pageSlug: string;
  color: string;
  enabled: boolean;
  name: string;
  runTime: string;
  model: AgentModel;
  lastRun: AgentRun | null;
  pending: number;
}

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Scrivi un orario come 07:30');

export const agentUpdateSchema = z
  .object({
    enabled: z.boolean(),
    name: z.string().trim().min(1, 'Dai un nome all’agente').max(60),
    instructions: z.string().trim().max(4000),
    routine: z.record(z.enum(AGENT_ROUTINE_IDS), z.boolean()),
    runTime: hhmm,
    model: z.enum(['sonnet', 'opus']),
    autonomous: z.boolean(),
    memory: z.string().max(30000),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Niente da salvare' });
export type AgentUpdateInput = z.infer<typeof agentUpdateSchema>;

export const agentActionSchema = z.object({
  name: z.string().trim().min(1, 'Dai un nome all’azione').max(60),
  description: z.string().trim().min(1, 'Scrivi a cosa serve, così l’agente sa quando usarla').max(500),
  url: z
    .string()
    .trim()
    .url('Incolla l’indirizzo del webhook (es. di n8n)')
    .refine((u) => /^https?:\/\//i.test(u), 'Solo indirizzi http o https'),
  needsConfirm: z.boolean().default(true),
  /** Valore dell'intestazione segreta (es. quella del webhook di n8n). */
  secret: z.string().trim().max(500).optional(),
});
export type AgentActionInput = z.infer<typeof agentActionSchema>;

export const agentDecisionSchema = z.object({ approve: z.boolean() });

/** Una riga dell'elenco Chat: l'agente di una scheda e l'ultimo messaggio. */
export interface AgentChatSummary {
  pageId: string;
  pageSlug: string;
  pageName: string;
  color: string;
  colorFg: string;
  agentName: string;
  enabled: boolean;
  /** Cose che l'agente ti chiede di approvare. */
  pending: number;
  conversationId: string | null;
  lastText: string | null;
  lastFromAgent: boolean;
  lastAt: string | null;
}
